import React from "react";
import { Button, Modal } from "../../../components/ui";
// The same mark the Services tab shows over its own empty grid — see
// `BrandServicesTab`. Deliberately the same one, for the reason
// `PlansRequiredModal` gives: this dialog is about that screen, and arriving
// there to find a different drawing over the same absence would read as a
// different subject.
import { ReactComponent as FirstServiceMark } from "../../../assets/svg/No data images/services.svg";

export interface ServicesRequiredModalProps {
  /** Takes them to the Services tab of My brand, which is where one is made. */
  onCreateService: () => void;
  /** The way out. Escape lands here too. */
  onDismiss: () => void;
  /**
   * What the way out reads as. "Not now" where they are being offered a detour
   * off a screen they can stay on; the screen behind a dialog they cannot use
   * without a service passes its own way back instead — "Back to my clients".
   */
  dismissLabel?: string;
}

/**
 * Raised when somebody reaches for Add client or New enquiry on a workspace
 * that has no service listed yet.
 *
 * `PlansRequiredModal`'s sibling, and every word of that file's reasoning
 * applies here — a dialog rather than a greyed button, written as the next step
 * rather than as a refusal, not dismissable by a stray click beside it.
 *
 * What is missing is the other half of the same sentence. A plan is what a
 * client pays for; a service is what they are working towards, and it is what
 * both forms' Goal field picks from. With none listed that field opens on an
 * empty list, and the form cannot be finished.
 *
 * Only ever one of the two dialogs is on screen: the screens raise this one
 * once there is something to sell, so a workspace on its first day is asked
 * for a plan first and for a service after — the order the My brand tabs
 * themselves are in.
 */
const ServicesRequiredModal: React.FC<ServicesRequiredModalProps> = ({
  onCreateService,
  onDismiss,
  dismissLabel = "Not now",
}) => (
  <Modal
    open
    // Names the step rather than the obstacle. It is also the dialog's
    // accessible name, which is why it reads as a sentence on its own.
    title="Add your first service"
    onClose={onDismiss}
    dismissOnBackdrop={false}
    // The heading is drawn in the body under the mark, so the panel reads as
    // one block rather than as a bar with a message beneath it. The `h2` stays
    // in the tree off screen as the dialog's name — see `Modal`'s `hideTitle`.
    hideTitle
    className="max-w-[460px]"
    footer={
      <>
        <Button variant="secondary" size="md" onClick={onDismiss}>
          {dismissLabel}
        </Button>
        {/* The one thing the dialog is for: it does not just say what is
            missing, it goes and gets it. The same words the Services tab's own
            empty state puts on the same action, so the button pressed here and
            the one waiting there read as the one step. */}
        <Button size="md" onClick={onCreateService}>
          Create service
        </Button>
      </>
    }
  >
    <div className="flex flex-col items-center text-center">
      {/* Decorative: the heading under it already says what this is. Drawn
          under its natural size — this is a mark over two lines of copy in a
          460px panel, not the illustration over an empty screen. */}
      <FirstServiceMark aria-hidden className="h-12 w-12" />

      <h3
        aria-hidden
        className="mt-4 text-[17px] 3xl:text-[19px] font-medium text-ink"
      >
        Add your first service
      </h3>

      <p className="mt-2 text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
        Add a service so you can say what each client is working towards. You
        can add more or edit them anytime.
      </p>
    </div>
  </Modal>
);

export default ServicesRequiredModal;
